import * as Api from '@ViewModels';
import { css } from 'aphrodite';
import * as React from 'react';
import { useUserSession } from '../../../../models/hooks/appStateHooks';
import { AutoReplyModeOptions } from './AutoReplyModeOptions';
import { ReviewAutoReplyPreview } from './ReviewAutoReplyPreview';
import { styleSheet } from './styles';
import { useGoogleReviewAutoReplySettingsForm } from './useGoogleReviewAutoReplySettingsForm';

interface IProps {
	impersonationContext?: Api.IImpersonationContext;
}

/**
 * Self-contained editor for the account-wide Google review AI auto-reply setting: fetches, stages edits,
 * saves through its own button, and offers the dry-run preview. Used where there is no surrounding form
 * (e.g. the Google Business integration page); the satisfaction survey editor wires the same hook into
 * its own save lifecycle instead.
 */
export const GoogleReviewAutoReplySettings: React.FC<IProps> = ({ impersonationContext }) => {
	const userSession = useUserSession();
	const isAiEnabled = !!(
		impersonationContext?.account?.features?.contentGeneration || userSession?.account?.features?.contentGeneration
	)?.enabled;

	const form = useGoogleReviewAutoReplySettingsForm({ enabled: isAiEnabled, impersonationContext });

	if (!isAiEnabled || !form.isLoaded) {
		return null;
	}

	return (
		<section>
			<h3 className={css(styleSheet.sectionTitle)}>AI Auto-Reply to Google Reviews</h3>
			<ReviewAutoReplyPreview
				impersonationContext={impersonationContext}
				settings={form.settings}
				onSaveSettings={form.saveSettings}
				isSavingSettings={form.isSaving}
				hasUnsavedSettings={form.hasUnsavedSettings}
			>
				<p className={css(styleSheet.accountWideCallout)}>
					<strong>This is an account-wide setting.</strong> It applies to every new Google review your account receives,
					no matter which survey generated it.
				</p>
				<AutoReplyModeOptions settings={form.settings} onChange={form.setPendingSettings} />
			</ReviewAutoReplyPreview>
		</section>
	);
};
